import React from 'react';
import { Alert as MuiAlert, AlertTitle, IconButton } from '@mui/material';
import type { AlertColor, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface AlertDialogProps {
  id: string;
  severity: AlertColor;
  title?: string;
  message: string;
  closeAlert: (id: string) => void;
  action?: React.ReactNode;
  sx?: SxProps;
  showCloseButton?: boolean;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  id,
  severity,
  title,
  message,
  closeAlert,
  action,
  sx = {},
  showCloseButton = true,
}) => {
  // Unconditionally close the alert when clicking the close button
  const handleClose = (event?: React.SyntheticEvent) => {
    if (event) event.stopPropagation(); // Prevent any parent click handlers from firing
    closeAlert(id);
  };

  // Create close button if showCloseButton is true
  const closeButton = showCloseButton ? (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={handleClose}
      style={{ marginLeft: 'auto' }} // Make sure it's aligned to the right
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  ) : null;

  // Combine user provided action with close button if needed
  const combinedAction = showCloseButton ? (
    <>
      {action}
      {closeButton}
    </>
  ) : action;

  return (
    <MuiAlert
      severity={severity}
      action={combinedAction}
      onClose={showCloseButton ? handleClose : undefined}
      sx={{
        width: '100%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        '& .MuiAlert-message': {
          display: 'flex',
          flexDirection: 'column',
        },
        '& .MuiAlert-action': {
          alignItems: 'center',
          paddingTop: '0px',
        },
        bgcolor: 
          severity === 'error' 
            ? 'rgba(211, 47, 47, 0.1)' 
            : severity === 'warning' 
            ? 'rgba(237, 108, 2, 0.1)' 
            : severity === 'info' 
            ? 'rgba(2, 136, 209, 0.1)' 
            : 'rgba(46, 125, 50, 0.1)',
        ...sx
      }}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </MuiAlert>
  );
};

export default AlertDialog;
